import React from 'react'
import {
  ACCOUNT_LOCKOUT,
  API,
  CHECK_EMAIL_STEP_ADDITIONAL_INFO_DEFAULT,
  CHECK_EMAIL_STEP_DESCRIPTION,
  CHECK_EMAIL_STEP_TITLE,
  REGISTER_LABELS,
  SIGNIN_LABELS,
  VALIDATION_MESSAGES,
} from '@constants'
import { serverUrl } from '@utils/MkEnv'
import { fieldValidators, validateField } from '@features/account/hooks/useRegistration/fieldValidators'
import { resolveMfaEmailForOktaEnrollProfile } from '@features/authentication/utils/resolveAuthnMfaDisplayEmail'
import { withStepDefaults } from '@typescript'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'
import { ProtectedDataStore } from '@utils/ProtectedDataStore'
import { AccountLockoutModal } from '@ui/AccountLockoutModal'
import { getLockoutVariant } from '@features/authentication/utils'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { useAuthLabelsContext } from '@features/account/contexts/AuthLabelsContext'
import { type RegisterStep, type RegistrationFormData, StepName } from './types'
import { fieldsForStep } from './validationConfig'

// Helper function to create AccountLockoutModal component with appropriate configuration
const createAccountLockedComponent = () => {
  const AccountLockoutStep: React.FC = () => {
    const authState = useAuthentication()
    const { getLabel } = useAuthLabelsContext()
    const lockoutVariant = getLockoutVariant(authState)

    const handleCallUs = () => {
      window.location.href = 'tel:1-800-765-4321'
    }

    const body =
      lockoutVariant === 'MFA_CRITICAL'
        ? getLabel('register', 'accountLock', 'mfaDescription', ACCOUNT_LOCKOUT.MFA_CRITICAL_BODY)
        : getLabel('register', 'accountLock', 'description', ACCOUNT_LOCKOUT.PASSWORD_BODY)
    const buttonLabel =
      lockoutVariant === 'MFA_CRITICAL'
        ? getLabel('register', 'accountLock', 'callUsButton', ACCOUNT_LOCKOUT.MFA_CRITICAL_BUTTON)
        : getLabel('register', 'accountLock', 'callUsButton', ACCOUNT_LOCKOUT.CALL_US_BUTTON)

    return React.createElement(AccountLockoutModal, {
      body,
      buttonLabel,
      buttonVariant: 'secondary',
      buttonAction: handleCallUs,
    })
  }

  return AccountLockoutStep
}

const collectStepErrors = (formData: RegistrationFormData, stepName: StepName): string[] => {
  const errors: string[] = []
  const fields = fieldsForStep[stepName] || []

  fields.forEach((name: string) => {
    if (!(name in fieldValidators)) {
      return
    }
    const value = (formData as any)[name]?.value
    const res = validateField(name, value, formData as any)
    if (!res.isValid) errors.push(res.message)
  })

  return errors
}

/**
 * Local helper to keep registration steps clean while remaining compatible with the
 * legacy IMkStep shape used by the booking flow.
 *
 * Note: Historically, steps carried per-step fieldSet/formState (book flow). Our registration
 * flow centralizes fields in its context and does not use those per-step structures. We inject
 * harmless defaults here to satisfy the shared step interface without littering the registry
 * with empty objects.
 *
 * TODO: Consider extracting a shared StepBase with optional fieldSet/formState and a shared
 * createStep helper in `shared/types`. That will let both features align without local helpers.
 */
type RegisterStepInput = Omit<RegisterStep, 'fieldSet' | 'formState'> & Partial<Pick<RegisterStep, 'fieldSet' | 'formState'>>
const createRegisterStep = (step: RegisterStepInput): RegisterStep => withStepDefaults(step as any) as RegisterStep

// Step registry configuration - using Record for O(1) name-based lookups
// Steps are ordered by their position in the registration flow
export const REGISTRATION_STEPS_BY_NAME: Record<StepName, RegisterStep> = {
  [StepName.ACCOUNT_LOOKUP]: createRegisterStep({
    // IMkStep required fields
    key: 'accountLookup',
    name: StepName.ACCOUNT_LOOKUP,
    title: SIGNIN_LABELS.ENTER_CONTRACT_DETAILS,
    status: 'pending',
    conditionals: {},
    content: {
      caption: REGISTER_LABELS.ACCOUNT_LOOKUP_CAPTION,
      title: REGISTER_LABELS.ACCOUNT_LOOKUP_TITLE,
      description: REGISTER_LABELS.ACCOUNT_LOOKUP_DESCRIPTION,
    },

    // RegisterStep additional fields
    // eslint-disable-next-line import/no-cycle
    props: {
      verificationTitle: REGISTER_LABELS.SELECT_VERIFICATION_METHOD,
      registerAsIndividualText: REGISTER_LABELS.REGISTER_AS_INDIVIDUAL,
      registerAsCompanyText: REGISTER_LABELS.REGISTER_AS_COMPANY_OR_TRUST,
      useRegistrantNameAsTitle: true, // Show user's name in title after account lookup succeeds
      // Header is always rendered by container; adjust appearance via configuration
      errorHandling: {
        validationErrors: 'inline', // Show validation errors inline within the step
        apiErrors: 'block', // Show API errors in main banner (blocks progression)
        preventProgression: true, // Block progression until validation passes
      },
      // sectionDescription: 'Please provide your contract information to verify your account',
      // verificationDescription: 'Please choose an option below to verify your personal account information.',
    },
    loader: () => import('./steps/_MkRegisterAccountLookup').then((m) => ({ default: m.MkRegisterAccountLookup })),
    isVisible: () => true,
    validation: (formData) => collectStepErrors(formData, StepName.ACCOUNT_LOOKUP).length === 0,
    apiConfig: {
      method: 'POST',
      endpoint: `${serverUrl}${API}/account/lookup`,
      headers: {},
      body: (formData: { [key: string]: any }) => ({
        membershipNumber: formData.membershipNumber ? formData.membershipNumber : null,
        companyName: formData.companyName ? formData.companyName : null,
        firstName: formData.firstName ? formData.firstName : null,
        lastName: formData.lastName ? formData.lastName : null,
        suffix: formData.suffix ? formData.suffix : null,
        phoneNumber: formData.phone?.phoneNumber ? formData.phone.phoneNumber : null,
        contractNumber: formData.contractNumber ? formData.contractNumber : null,
        verificationMethod: formData.verificationMethod ? formData.verificationMethod : null,
        registerAsCompany: formData.registerAsCompany ? formData.registerAsCompany : null,
      }),
      params: {},
      onSuccess: async (data) => {
        // Handle account lookup success
        if (data?.party?.email) {
          // This will be handled in the submit function
        }
        if (data?.party?.partyStatus) {
          // This will be handled in the submit function
        }
      },
    },
  }),
  [StepName.USER_DETAILS]: createRegisterStep({
    // IMkStep required fields
    key: 'userDetails',
    name: StepName.USER_DETAILS,
    title: REGISTER_LABELS.USER_DETAILS_TITLE,
    status: 'pending',
    conditionals: {},
    content: {
      caption: REGISTER_LABELS.USER_DETAILS_CAPTION,
      title: REGISTER_LABELS.USER_DETAILS_TITLE,
      description: REGISTER_LABELS.USER_DETAILS_DESCRIPTION,
    },

    props: {
      description: REGISTER_LABELS.USER_DETAILS_DESCRIPTION,
      useRegistrantNameAsTitle: true,
      // Header is always rendered by container; adjust appearance via configuration
      errorHandling: {
        validationErrors: 'block', // Show validation errors in main banner (blocks progression)
        apiErrors: 'block', // Show API errors in main banner (blocks progression)
        preventProgression: true, // Block progression with errors (required step)
      },
    },
    loader: () => import('./steps/_MkRegisterSubmitUserDetails').then((m) => ({ default: m.MkRegisterSubmitUserDetails })),
    isVisible: () => true,
    validation: (formData) => collectStepErrors(formData, StepName.USER_DETAILS).length === 0,
    apiConfig: {
      method: 'POST',
      endpoint: `${serverUrl}${API}/account/register`,
      headers: {},
      body: (formData, registrationToken) => {
        if (!formData.email?.value) {
          throw new Error(VALIDATION_MESSAGES.EMAIL_REQUIRED)
        }
        if (!formData.userName?.value) {
          throw new Error(VALIDATION_MESSAGES.USERNAME_REQUIRED)
        }

        return {
          email: formData.email.value,
          username: formData.userName.value,
          registrationToken,
        }
      },
      params: {},
    },
  }),
  [StepName.CHECK_EMAIL]: createRegisterStep({
    key: 'checkEmail',
    name: StepName.CHECK_EMAIL,
    title: CHECK_EMAIL_STEP_TITLE,
    status: 'pending',
    conditionals: {},
    content: {
      title: CHECK_EMAIL_STEP_TITLE,
      imageSrc: '/content/images/sir/check_your_email.gif',
      imageAlt: CHECK_EMAIL_STEP_TITLE,
      imageWidth: '160px',
      imageHeight: '160px',
    },
    props: {
      description: CHECK_EMAIL_STEP_DESCRIPTION,
      additionalInfo: CHECK_EMAIL_STEP_ADDITIONAL_INFO_DEFAULT,
      // Header is always rendered by container; adjust appearance via configuration
      errorHandling: {
        validationErrors: 'none', // No validation errors for this step
        apiErrors: 'block', // Show API errors in main banner
        preventProgression: true, // Block progression with errors
      },
    },
    // simple presentational step with no API side-effect on submit
    loader: () => import('./steps/_MkRegisterCheckEmail').then((m) => ({ default: m.MkRegisterCheckEmail })),
    isVisible: () => true,
    validation: () => true,
    apiConfig: undefined,
  }),
  [StepName.SET_PASSWORD]: createRegisterStep({
    // IMkStep required fields
    key: 'setPassword',
    name: StepName.SET_PASSWORD,
    title: REGISTER_LABELS.SET_PASSWORD_TITLE,
    status: 'pending',
    conditionals: {},
    content: {
      caption: REGISTER_LABELS.SET_PASSWORD_CAPTION,
      title: REGISTER_LABELS.SET_PASSWORD_TITLE,
      description: REGISTER_LABELS.SET_PASSWORD_DESCRIPTION,
    },

    // RegisterStep additional fields
    // eslint-disable-next-line import/no-cycle
    loader: () => import('./steps/_MkRegisterSetPassword').then((m) => ({ default: m.MkRegisterSetPassword })),
    isVisible: () => true,
    validation: (formData) => collectStepErrors(formData, StepName.SET_PASSWORD).length === 0,
    props: {
      description: REGISTER_LABELS.SET_PASSWORD_DESCRIPTION,
      useRegistrantNameAsTitle: true,
      // Header is always rendered by container; adjust appearance via configuration
      errorHandling: {
        validationErrors: 'block', // Show validation errors in main banner
        apiErrors: 'block', // Show API errors in main banner
        preventProgression: true, // Block progression with errors (required step)
      },
    },
    apiConfig: {
      method: 'POST',
      endpoint: (() => `${serverUrl}${API}/okta/authn/credentials/reset_password`)(),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: (formData, registrationToken, stateToken) => {
        const stateTokenToUse = stateToken || DataStore.getItem('stateToken')

        if (!stateTokenToUse) {
          throw new Error(VALIDATION_MESSAGES.NO_AUTH_TOKEN)
        }

        if (!formData.password?.value) {
          throw new Error(VALIDATION_MESSAGES.PASSWORD_REQUIRED)
        }

        // For Okta password reset API, use the stateToken (from loginWithToken) or registrationToken
        const payload: any = {
          stateToken: stateTokenToUse,
          newPassword: formData.password.value,
        }

        return payload
      },
      params: {},
    },
  }),
  [StepName.MFA_SETUP_SELECTOR]: createRegisterStep({
    key: 'mfaSetupSelector',
    name: StepName.MFA_SETUP_SELECTOR,
    title: REGISTER_LABELS.SELECT_MFA_METHOD,
    status: 'pending',
    conditionals: {},
    content: {
      caption: REGISTER_LABELS.WELCOME_BACK_EYE_BROW,
      // title: REGISTER_LABELS.SET_UP_MFA,
    },
    props: {
      icon: 'envelope',
      useRegistrantNameAsTitle: true,
      // Header is always rendered by container; adjust appearance via configuration
      errorHandling: {
        validationErrors: 'inline',
        apiErrors: 'block',
        preventProgression: false,
      },
    },
    loader: () => import('./steps/_MkRegisterMfaSetupSelector').then((m) => ({ default: m.MkRegisterMfaSetupSelector })),
    isVisible: () => true,
    validation: (formData) => collectStepErrors(formData, StepName.MFA_SETUP_SELECTOR).length === 0,
    // No apiConfig - this step just selects the MFA method and navigates to details
  }),
  [StepName.MFA_SETUP_DETAILS]: createRegisterStep({
    key: 'mfaSetupDetails',
    name: StepName.MFA_SETUP_DETAILS,
    title: REGISTER_LABELS.MFA_SETUP_DETAILS_TITLE,
    status: 'pending',
    conditionals: {},
    content: {
      title: REGISTER_LABELS.MFA_SETUP_DETAILS_TITLE,
    },
    props: {
      factorType: 'phone',
      headerTitleClassName: 'text-3xl font-bold',
      emailDescription: REGISTER_LABELS.MFA_SETUP_EMAIL_DESCRIPTION,
      phoneDescription: REGISTER_LABELS.MFA_SETUP_PHONE_DESCRIPTION,
      emailAddressLabel: REGISTER_LABELS.MFA_EMAIL_ADDRESS_LABEL,
      emailPlaceholder: REGISTER_LABELS.MFA_EMAIL_PLACEHOLDER,
      continueButton: REGISTER_LABELS.MFA_CONTINUE_TO_SIGNIN,
      // Header is always rendered by container; adjust appearance via configuration
      errorHandling: {
        validationErrors: 'inline',
        apiErrors: 'block',
        preventProgression: false,
      },
    },
    loader: () => import('./steps/_MkRegisterMfaSetupDetails').then((m) => ({ default: m.MkRegisterMfaSetupDetails })),
    isVisible: () => true,
    validation: (formData) => collectStepErrors(formData, StepName.MFA_SETUP_DETAILS).length === 0,
    apiConfig: {
      method: 'POST',
      endpoint: (() => `${serverUrl}${API}/okta/authn/factors`)(),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: (formData, registrationToken, stateToken, selectedFactor) => {
        // Use the selected factor passed from the provider
        const factor = selectedFactor || { factorType: 'sms', provider: 'OKTA' }

        const phone = formData.phone?.value
        const phoneCountry = formData.phoneCountry?.value || 'USA'
        // Format phone number for Okta (remove all non-digits)
        const numericPhone = phone ? phone.replace(/\D/g, '') : ''

        // Get registration context to access the stateToken
        let stateTokenToUse = stateToken

        // If stateToken is not provided directly, try to get it from registration context
        let registrationContext: any = null
        if (!stateTokenToUse) {
          registrationContext = ProtectedDataStore.getItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, {
            store: localStorage,
          })

          if (registrationContext?.stateToken) {
            stateTokenToUse = registrationContext.stateToken
          }
        }

        // Fallback to legacy storage if still no token
        if (!stateTokenToUse) {
          const legacyStateToken = DataStore.getItem('stateToken')
          stateTokenToUse = legacyStateToken
        }

        if (!stateTokenToUse) {
          // For MFA_ENROLL flow, we might need to make a call to get the stateToken
          // This could happen if the stateToken wasn't available in the initial authn response

          // We could potentially make a call to Okta's authn API here to get a stateToken
          // But for now, let's throw a more descriptive error
          throw new Error(REGISTER_LABELS.MFA_NO_STATE_TOKEN_ERROR)
        }

        if (!registrationContext) {
          registrationContext = ProtectedDataStore.getItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, {
            store: localStorage,
          })
        }

        // Okta email factor requires a real mailbox in profile.email, not the user's login id stored as context.email
        const emailToUse = factor.factorType === 'email' ? resolveMfaEmailForOktaEnrollProfile(formData, registrationContext) : ''

        if (factor.factorType === 'email' && !emailToUse) {
          throw new Error('Email address is required for email MFA')
        }

        const payload: any = {
          factorType: factor.factorType,
          provider: factor.provider || 'OKTA',
          stateToken: stateTokenToUse,
        }

        // Add profile based on factor type
        if (factor.factorType === 'email') {
          payload.profile = {
            email: emailToUse,
          }
        } else if (factor.factorType === 'sms' || factor.factorType === 'call') {
          // Format phone number with proper country code
          let formattedPhone = numericPhone
          if (phoneCountry === 'USA' || phoneCountry === 'US') {
            formattedPhone = `+1${numericPhone}`
          } else {
            // For other countries, you might need to add proper country code logic
            formattedPhone = `+1${numericPhone}` // Default to US for now
          }

          payload.profile = {
            phoneNumber: formattedPhone,
          }
        }

        return payload
      },
      params: {},
    },
  }),
  [StepName.MFA_VERIFY_CODE]: createRegisterStep({
    key: 'mfaVerifyCode',
    name: StepName.MFA_VERIFY_CODE,
    title: REGISTER_LABELS.MFA_VERIFY_CODE_TITLE,
    status: 'pending',
    conditionals: {},
    content: {
      caption: REGISTER_LABELS.MFA_VERIFY_CODE_CAPTION,
      title: REGISTER_LABELS.MFA_VERIFY_CODE_STEP_TITLE,
      description: REGISTER_LABELS.MFA_VERIFY_CODE_DESCRIPTION,
      codeSentMessage: REGISTER_LABELS.MFA_CODE_SENT_MESSAGE,
    },
    props: {
      // Match Figma: large display heading (same scale as MFA setup details step)
      headerTitleClassName: 'text-3xl font-bold',
      authorizationCodeLabel: REGISTER_LABELS.MFA_AUTHORIZATION_CODE_LABEL,
      resendCodeButton: REGISTER_LABELS.MFA_RESEND_CODE_BUTTON,
      resendingButton: REGISTER_LABELS.MFA_RESENDING_BUTTON,
      verifyButton: REGISTER_LABELS.MFA_VERIFY_CONTINUE,
      codeResentSuccess: REGISTER_LABELS.MFA_CODE_RESENT_SUCCESS,
      ariaResendLabel: REGISTER_LABELS.ARIA_RESEND_VERIFICATION_CODE,
      // Header is always rendered by container; adjust appearance via configuration
      errorHandling: {
        validationErrors: 'inline', // Field-level (ZinInput)
        apiErrors: 'inline', // Map API failures to verificationCode; no duplicate FormError banner
        preventProgression: true, // Block progression with errors (required step)
      },
    },
    loader: () => import('./steps/_MkRegisterMfaVerifyCode').then((m) => ({ default: m.MkRegisterMfaVerifyCode })),
    isVisible: () => true,
    validation: (formData) => collectStepErrors(formData, StepName.MFA_VERIFY_CODE).length === 0,
    apiConfig: {
      method: 'POST',
      endpoint: (formData) => {
        // Get registration context to determine flow type
        const registrationContext = ProtectedDataStore.getItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, {
          store: localStorage,
        })

        // Check if this is a login flow enrollment that requires confirmMfa
        // This happens when:
        // 1. Enrollment response had status MFA_ENROLL_ACTIVATE (stored as isMfaActivationFlow)
        // 2. OR user has a userName (logged in) and no activationToken (not an activation flow)
        const isLoginFlowEnrollment =
          (registrationContext?.isMfaActivationFlow ||
            (registrationContext?.userName && !registrationContext?.activationToken && registrationContext?.verifyLink && !registrationContext?.isActivationFlow)) &&
          registrationContext?.verifyLink

        if (isLoginFlowEnrollment && registrationContext.verifyLink) {
          // Use confirmMfa endpoint for login flow enrollments
          return `${serverUrl}${API}/okta/authn/confirm`
        }

        // Priority 1: Check registration context for verifyLink (for other flows)
        if (registrationContext?.verifyLink) {
          return registrationContext.verifyLink
        }

        // Priority 2: Fallback to legacy DataStore
        const storedNextLink = DataStore.getItem('mfaNextLink')
        if (storedNextLink) {
          return storedNextLink
        }

        // Priority 3: Fallback to the default endpoint structure
        const factorId = formData.mfaFactorId?.value || registrationContext?.mfaFactorId
        if (!factorId) {
          throw new Error('No MFA factorId available to construct verification URL.')
        }
        return `${serverUrl}${API}/okta/authn/factors/${factorId}/verify`
      },
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: (formData, registrationToken, stateToken) => {
        const code = formData.verificationCode?.value

        // Get registration context to access the stateToken and verifyLink
        const registrationContext = ProtectedDataStore.getItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, {
          store: localStorage,
        })

        // Check if this is a login flow enrollment
        const isLoginFlowEnrollment =
          (registrationContext?.isMfaActivationFlow ||
            (registrationContext?.userName && !registrationContext?.activationToken && registrationContext?.verifyLink && !registrationContext?.isActivationFlow)) &&
          registrationContext?.verifyLink

        // Get registration context to access the stateToken
        let stateTokenToUse = stateToken

        // If stateToken is not provided directly, try to get it from registration context
        if (!stateTokenToUse) {
          if (registrationContext?.stateToken) {
            stateTokenToUse = registrationContext.stateToken
          }
        }

        // Fallback to legacy storage if still no token
        if (!stateTokenToUse) {
          stateTokenToUse = DataStore.getItem('stateToken')
        }

        // For login flow enrollments, use confirmMfa format with linkUrl
        if (isLoginFlowEnrollment && registrationContext?.verifyLink) {
          return {
            passCode: code,
            stateToken: stateTokenToUse,
            linkUrl: registrationContext.verifyLink,
          }
        }

        // For other flows, use standard verify format
        return {
          passCode: code,
          stateToken: stateTokenToUse,
        }
      },
      params: {},
    },
  }),
  [StepName.ACCOUNT_LOCK_SCREEN]: createRegisterStep({
    key: 'accountLockScreen',
    name: StepName.ACCOUNT_LOCK_SCREEN,
    title: REGISTER_LABELS.ACCOUNT_LOCK_TITLE,
    status: 'pending',
    conditionals: {},
    content: {
      caption: REGISTER_LABELS.ACCOUNT_LOCK_CAPTION,
      title: REGISTER_LABELS.ACCOUNT_LOCK_TITLE,
      icon: ACCOUNT_LOCKOUT.ICON_NAME,
    },
    props: {
      headerIcon: 'lock-keyhole',
      headerIconSet: 'fal',
      headerIconClassName: 'form-header__icon--large',
      mfaWarningMessage: REGISTER_LABELS.ACCOUNT_LOCK_MFA_WARNING,
      loginWarningMessage: REGISTER_LABELS.ACCOUNT_LOCK_LOGIN_WARNING,
      mfaDescriptionMessage: REGISTER_LABELS.ACCOUNT_LOCK_MFA_DESCRIPTION,
      loginDescriptionMessage: REGISTER_LABELS.ACCOUNT_LOCK_LOGIN_DESCRIPTION,
      callUsButtonText: REGISTER_LABELS.CALL_US_BUTTON,
      // Header is always rendered by container; adjust appearance via configuration
      errorHandling: {
        validationErrors: 'none',
        apiErrors: 'none',
        preventProgression: true,
      },
    },
    loader: () => Promise.resolve({ default: createAccountLockedComponent() }),
    isVisible: () => true,
    validation: () => true,
    apiConfig: undefined,
  }),
  [StepName.MFA_CRITICAL_LOCK_SCREEN]: createRegisterStep({
    key: 'mfaCriticalLockScreen',
    name: StepName.MFA_CRITICAL_LOCK_SCREEN,
    title: ACCOUNT_LOCKOUT.MFA_CRITICAL_TITLE,
    status: 'pending',
    conditionals: {},
    content: {
      caption: ACCOUNT_LOCKOUT.MFA_CRITICAL_CAPTION,
      title: ACCOUNT_LOCKOUT.MFA_CRITICAL_TITLE,
      icon: ACCOUNT_LOCKOUT.ICON_NAME,
    },
    props: {
      headerIcon: 'lock-keyhole',
      headerIconSet: 'fal',
      headerIconClassName: 'form-header__icon--large',
      // Header is always rendered by container; adjust appearance via configuration
      errorHandling: {
        validationErrors: 'none',
        apiErrors: 'none',
        preventProgression: true,
      },
    },
    loader: () => Promise.resolve({ default: createAccountLockedComponent() }),
    isVisible: () => true,
    validation: () => true,
    apiConfig: undefined,
  }),
  [StepName.ACTIVATION_ERROR]: createRegisterStep({
    key: 'activationError',
    name: StepName.ACTIVATION_ERROR,
    title: REGISTER_LABELS.ACTIVATION_ERROR_TITLE,
    status: 'pending',
    conditionals: {},
    content: {
      caption: REGISTER_LABELS.ACTIVATION_ERROR_CAPTION,
      title: REGISTER_LABELS.ACTIVATION_ERROR_STEP_TITLE,
      description: REGISTER_LABELS.ACTIVATION_ERROR_DESCRIPTION,
    },
    props: {
      tryAgainText: REGISTER_LABELS.TRY_AGAIN,
      errorFallbackMessage: REGISTER_LABELS.ERROR_OCCURRED_FALLBACK,
      // Header is always rendered by container; adjust appearance via configuration
      errorHandling: {
        validationErrors: 'none',
        apiErrors: 'none',
        preventProgression: true,
      },
    },
    loader: () => import('./steps/_MkRegisterActivationError').then((m) => ({ default: m.MkRegisterActivationError })),
    isVisible: () => true,
    validation: () => true,
    apiConfig: undefined,
  }),
}

export const getStepByName = (stepName: StepName): RegisterStep | undefined => REGISTRATION_STEPS_BY_NAME[stepName]

// Get steps in order for navigation
export const getStepsInOrder = (): RegisterStep[] => [
  REGISTRATION_STEPS_BY_NAME[StepName.ACCOUNT_LOOKUP],
  REGISTRATION_STEPS_BY_NAME[StepName.USER_DETAILS],
  REGISTRATION_STEPS_BY_NAME[StepName.CHECK_EMAIL],
  REGISTRATION_STEPS_BY_NAME[StepName.SET_PASSWORD],
  REGISTRATION_STEPS_BY_NAME[StepName.MFA_SETUP_SELECTOR],
  REGISTRATION_STEPS_BY_NAME[StepName.MFA_SETUP_DETAILS],
  REGISTRATION_STEPS_BY_NAME[StepName.MFA_VERIFY_CODE],
  REGISTRATION_STEPS_BY_NAME[StepName.ACCOUNT_LOCK_SCREEN],
  REGISTRATION_STEPS_BY_NAME[StepName.MFA_CRITICAL_LOCK_SCREEN],
  REGISTRATION_STEPS_BY_NAME[StepName.ACTIVATION_ERROR],
]

export const getStepIndexByName = (stepName: StepName): number => {
  const steps = getStepsInOrder()
  return steps.findIndex((step) => step.name === stepName)
}

export const getStepNames = (): StepName[] => [
  StepName.ACCOUNT_LOOKUP,
  StepName.USER_DETAILS,
  StepName.CHECK_EMAIL,
  StepName.SET_PASSWORD,
  StepName.MFA_SETUP_SELECTOR,
  StepName.MFA_SETUP_DETAILS,
  StepName.MFA_VERIFY_CODE,
  StepName.ACCOUNT_LOCK_SCREEN,
  StepName.MFA_CRITICAL_LOCK_SCREEN,
  StepName.ACTIVATION_ERROR,
]

export const validateStepByName = (stepName: StepName, formData: RegistrationFormData): boolean => {
  const step = REGISTRATION_STEPS_BY_NAME[stepName]
  if (!step) {
    return false
  }

  if (step.props?.errorHandling?.preventProgression === false) {
    return true
  }

  const errors = collectStepErrors(formData, stepName)

  return errors.length === 0
}

export const getStepValidationErrorsByName = (stepName: StepName, formData: RegistrationFormData): string[] => {
  const step = REGISTRATION_STEPS_BY_NAME[stepName]
  if (!step) return ['Step not found']
  return collectStepErrors(formData, stepName)
}

export const getStepTitleByName = (stepName: StepName): string => {
  const step = REGISTRATION_STEPS_BY_NAME[stepName]
  return step?.title || 'Untitled Step'
}

const getStepContentValue = (value: string | string[] | undefined): string => (Array.isArray(value) ? value.join(' ') : value || '')

export const getStepDescriptionByName = (stepName: StepName): string => {
  const step = REGISTRATION_STEPS_BY_NAME[stepName]
  return getStepContentValue(step?.content?.description)
}
